import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { forwardRef, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { CatalogWriteError } from '../_lib/catalogErrors';
import { NAME_MAX } from '../_lib/catalogForms';

/**
 * A catalogue name input (AUT-437): `maxLength` 50 + an «N / 50» counter,
 * the Zod error (an i18n key), and a server outcome — a 409 conflict names
 * the holder with a link that opens its make (hidden when it is the make
 * already open), a race without details reads «имя занято»; a 422 reads
 * «не длиннее 50».
 */
export type FieldServerError = Extract<CatalogWriteError, { kind: 'conflict' | 'tooLong' }>;

export interface NameFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  errorKey?: string | undefined;
  serverError: FieldServerError | null;
  /** Open the make that holds the name; `currentMakeId` hides the link for that make. */
  onOpenMake?: (makeId: string) => void;
  currentMakeId?: string;
  disabled?: boolean;
  /** Focus when mounted — only in a dialog / inline form the admin just opened. */
  focusOnMount?: boolean;
  helper?: ReactNode;
}

const ServerErrorText = ({
  error,
  onOpenMake,
  currentMakeId,
}: {
  error: FieldServerError;
  onOpenMake: NameFieldProps['onOpenMake'];
  currentMakeId: string | undefined;
}): ReactElement => {
  const { t } = useTranslation();
  if (error.kind === 'tooLong') return <>{t('vehicleCatalog.validation.tooLong')}</>;
  const { holder } = error;
  if (!holder) return <>{t('vehicleCatalog.conflict.generic')}</>;
  const key = error.code === 'catalog_alias_conflict' ? 'alias' : 'name';
  const showLink = onOpenMake !== undefined && holder.makeId !== currentMakeId;
  return (
    <Box component="span" sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
      <span>{t(`vehicleCatalog.conflict.${key}`, { name: holder.name })}</span>
      {showLink ? (
        <Link component="button" type="button" onClick={() => onOpenMake(holder.makeId)}>
          {t('vehicleCatalog.conflict.open', { name: holder.name })}
        </Link>
      ) : null}
    </Box>
  );
};

export const NameField = forwardRef<HTMLInputElement, NameFieldProps>(function NameField(
  {
    label,
    value,
    onChange,
    onBlur,
    errorKey,
    serverError,
    onOpenMake,
    currentMakeId,
    disabled,
    focusOnMount,
    helper,
  },
  ref,
) {
  const { t } = useTranslation();
  const hasError = Boolean(errorKey) || serverError !== null;
  const helperText = serverError ? (
    <ServerErrorText error={serverError} onOpenMake={onOpenMake} currentMakeId={currentMakeId} />
  ) : errorKey ? (
    t(errorKey)
  ) : (
    helper
  );
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
          {t('vehicleCatalog.name.counter', { len: value.length, max: NAME_MAX })}
        </Typography>
      </Box>
      <TextField
        inputRef={ref}
        fullWidth
        size="small"
        required
        label={label}
        value={value}
        disabled={disabled}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- a dialog the admin just opened
        autoFocus={focusOnMount}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        error={hasError}
        helperText={helperText}
        slotProps={{
          htmlInput: { maxLength: NAME_MAX },
          formHelperText: { component: 'div' },
        }}
      />
    </Box>
  );
});
